import { firestore } from "firebase/app";
import {
  toEndOfDay,
  toStartOfDay,
} from "@/utils/DateHelper";
import { RecordInfo } from "./RecordInfo";
import { isProgramChainAssignedTo, ProgramChain } from "./ProgramChain";
import { DeviceProjectAssignment } from "./DeviceProjectAssignment";
import { shouldHideSerialNumber } from "../repository/ClientRepository";

export interface MeterReadingInfo {
  meterReadingStartKWh?: number;
  meterReadingEndKWh?: number;

  meterReadingStartCorrectedBy?: RecordInfo;
  meterReadingEndCorrectedBy?: RecordInfo;
}

export interface ClientPanelAssignment extends DeviceProjectAssignment {
  wasSingedOffByUser: boolean | null;
  assignedProjectNumber: string;
  serialNumber: string;
  inventoryNumber?: string;
  meterReadingInfo: MeterReadingInfo | null;
  powerConsumptionInKWh: number | null;
  createdBy?: RecordInfo;
}

export function doesAssignmentIncludeProgramChain(
  assignment: ClientPanelAssignment,
  programChain: ProgramChain
) {
  if (assignment.assignmentEndDate) {
    return isProgramChainAssignedTo(programChain, assignment.id);
  } else {
    if (
      programChain.assignedProjectDetails &&
      programChain.assignedProjectDetails.assignmentId !== assignment.id
    ) {
      return false;
    }

    return (
      assignment.assignmentStartDate.toMillis() <=
      programChain.startDate.toMillis()
    );
  }
}

export function isDateIncludedInOtherAssignment(
  date: firestore.Timestamp,
  assignment: DeviceProjectAssignment
) {
  const startOfDay = toStartOfDay(date);

  if (startOfDay.toMillis() <= assignment.assignmentStartDate.toMillis()) {
    return false;
  }

  if (assignment.assignmentEndDate) {
    const endOfDay = toEndOfDay(date);

    return endOfDay.toMillis() < assignment.assignmentEndDate.toMillis();
  }

  return true;
}

export function doClientPanelAssignmentsCollide(
  leftStartDate: firestore.Timestamp,
  leftEndDate: firestore.Timestamp | null,
  right: ClientPanelAssignment
) {
  const rightStartDate = right.assignmentStartDate;
  const rightEndDate = right.assignmentEndDate;

  if (!rightEndDate) {
    return leftStartDate.toMillis() >= rightStartDate.toMillis();
  }

  if (rightEndDate.toMillis() <= toEndOfDay(leftStartDate).toMillis()) {
    //Right assignment ended before start of left assignment
    return false;
  }

  //Right assignment ended after start of left assignment
  if (leftEndDate) {
    if (leftEndDate.toMillis() > toEndOfDay(rightStartDate).toMillis()) {
      //Left assignment ended after start of right assignment
      return true;
    }
  } else {
    if (rightStartDate.toMillis() < leftStartDate.toMillis()) {
      //Right assignment started before left assignment
      return true;
    }
  }

  return false;
}

export function getClientPanelDisplayNameFromAssignment(
  ClientPanelAssignment: ClientPanelAssignment,
  divider?: string,
) {
  const serialNumber = ClientPanelAssignment.serialNumber;
  const inventoryNumber = ClientPanelAssignment.inventoryNumber;

  if (shouldHideSerialNumber()) {
    return inventoryNumber;
  }

  if (!inventoryNumber || inventoryNumber === "" || inventoryNumber === serialNumber) {
    return serialNumber;
  }

  return `${inventoryNumber}${divider ?? " / "}${serialNumber}`;
}